import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Leaf, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const QUICK_ACTIONS = [
  { label: "Find the best microgreen for me", icon: "🌱" },
  { label: "Microgreens benefits", icon: "💪" },
  { label: "Recipe ideas", icon: "🍽" },
  { label: "Storage tips", icon: "❄" },
  { label: "Browse products", icon: "🛒" },
];

function ProductCard({ product }) {
  return (
    <Link
      to={`/products/${product.slug}`}
      className="flex gap-3 bg-white rounded-xl p-3 border border-praroha-sage/20 hover:border-praroha-sage/40 hover:shadow-sm transition-all group"
      data-testid={`chat-product-${product.slug}`}
    >
      <img
        src={product.image}
        alt={product.name}
        className="w-16 h-16 rounded-lg object-cover shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="font-heading text-sm font-semibold text-praroha-forest group-hover:text-praroha-sage transition-colors truncate">
          {product.name}
        </p>
        <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
          {product.short_description}
        </p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs font-semibold text-praroha-forest">
            <span className="text-praroha-terracotta">₹{product.price}</span>/{product.weight}
          </span>
          <span className="text-[10px] text-praroha-sage font-medium flex items-center gap-0.5 group-hover:underline">
            View Product <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div className={`max-w-[85%] ${isUser ? "order-1" : ""}`}>
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-praroha-forest flex items-center justify-center">
              <span className="text-xs">🌱</span>
            </div>
            <span className="text-[10px] text-muted-foreground font-medium">Ankura</span>
          </div>
        )}
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
            isUser
              ? "bg-praroha-forest text-white rounded-br-md"
              : "bg-praroha-cream/60 text-praroha-forest rounded-bl-md"
          }`}
        >
          <FormattedText text={msg.content} />
        </div>
        {msg.product_cards?.length > 0 && (
          <div className="mt-2 space-y-2">
            {msg.product_cards.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        const boldParsed = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        if (line.trim().startsWith("- ") || line.trim().startsWith("• ")) {
          return (
            <div key={i} className="flex gap-1.5 items-start">
              <span className="text-praroha-sage mt-0.5 shrink-0">•</span>
              <span dangerouslySetInnerHTML={{ __html: boldParsed.replace(/^[-•]\s*/, "") }} />
            </div>
          );
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: boldParsed }} />;
      })}
    </div>
  );
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const lastAssistantRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Scroll to the start of the latest assistant message so user sees the answer from the top
    if (lastAssistantRef.current) {
      lastAssistantRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
      });
      const { response, session_id, product_cards } = res.data;
      setSessionId(session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response, product_cards },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I'm having trouble right now. Please try again!",
          product_cards: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (label) => {
    sendMessage(label);
  };

  const showGreeting = messages.length === 0;

  return (
    <>
      {/* Chat toggle button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-center gap-1.5"
            data-testid="chat-toggle-btn"
          >
            <span className="hidden sm:block text-[10px] font-medium text-praroha-forest bg-white px-2.5 py-1 rounded-full shadow-md border border-border/50 whitespace-nowrap">
              Ask me anything
            </span>
            <div className="w-12 h-12 rounded-full bg-praroha-forest text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center">
              <span className="text-2xl">🌱</span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[380px] sm:max-w-[380px] h-[100dvh] sm:h-[560px] sm:max-h-[560px] bg-white sm:rounded-2xl shadow-2xl border border-border/60 flex flex-col overflow-hidden"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-praroha-forest px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                  <span className="text-xl">🌱</span>
                </div>
                <div>
                  <h3 className="text-white font-heading font-semibold text-sm">
                    Ankura - Praroha Greens Assistant
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-praroha-cream/20 to-white">
              {showGreeting && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="text-center py-3">
                    <div className="w-12 h-12 rounded-full bg-praroha-forest/10 flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl">🌱</span>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-praroha-forest">
                      Hello!
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      I'm Ankura, your Microgreens Assistant.
                      <br />
                      How can I help you today?
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {QUICK_ACTIONS.map((action) => (
                      <button
                        key={action.label}
                        onClick={() => handleQuickAction(action.label)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-praroha-sage/20 rounded-full text-xs font-medium text-praroha-forest hover:border-praroha-sage/40 hover:bg-praroha-cream/40 transition-all"
                        data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span>{action.icon}</span>
                        {action.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {messages.map((msg, i) => {
                // Attach ref to the last assistant message so we scroll to its top
                const isLastAssistant =
                  msg.role === "assistant" &&
                  i === messages.map((m) => m.role).lastIndexOf("assistant");
                return (
                  <div key={i} ref={isLastAssistant ? lastAssistantRef : null}>
                    <MessageBubble msg={msg} />
                  </div>
                );
              })}

              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <div className="w-5 h-5 rounded-full bg-praroha-forest flex items-center justify-center">
                    <span className="text-xs">🌱</span>
                  </div>
                  <div className="bg-praroha-cream/60 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-praroha-sage/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 rounded-full bg-praroha-sage/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 rounded-full bg-praroha-sage/40 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <form
              onSubmit={handleSubmit}
              className="px-4 py-3 border-t border-border/50 bg-white shrink-0"
            >
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about microgreens..."
                  disabled={loading}
                  className="flex-1 bg-praroha-cream/30 border border-border/50 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-praroha-sage/50 focus:bg-white transition-all placeholder:text-muted-foreground/60"
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-10 h-10 rounded-full bg-praroha-forest text-white flex items-center justify-center hover:bg-praroha-forest/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
                  data-testid="chat-send-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
